/**
 * @module Permission/Model
 */

import { NavigationInfo } from '@app/models/navigation.model';
import { CompanyMemberRole, CompanyType } from '@feature/companies/shared';
import { PermissionContextService } from '@app/services/permissions/permission-context.service';
import { PermissionValidationService } from '@app/services/permissions/permission-validation.service';
import { ACTION_REQUIRED_QUEUE_NAME } from '@feature/orders/shared';

/**
 * Defines an enum for better permissions readability and type safety
 */
export enum PermissionMode {
	REMOVE = 'remove',
	DISABLE = 'disable'
}

/**
 * Type representing a function that checks permissions based on the provided context.
 *
 * This type defines a function signature that takes a PermissionContextService instance as an argument
 * and returns a promise that resolves to a boolean value indicating whether the permission check was successful.
 *
 * @type {function(PermissionContextService): Promise<boolean>}
 */
export type PermissionFunction = (context: PermissionContextService) => Promise<boolean>;

/**
 * Class representing a collection of permissions associated with various keys.
 *
 * This class allows for dynamic access to permission functions based on their keys name. Each property
 * in the object corresponds to a permission function that can be invoked with a specific context.
 *
 * @class
 * @model
 */
export class PermissionsObject {
    [name: string]: PermissionFunction;
}

/**
 * Interface representing a model for permission types, including roles and associated permissions.
 *
 * This interface defines a structure where each permission type can have multiple roles, and each role
 * can have an array of associated permissions. It is used to manage and organize permissions within
 * the application.
 *
 * @interface
 */
export interface PermissionTypesObject {
    [type: string]: {
        [role: string]: Array<PermissionsObject>
    };
}

/**
 * Constant representing the application-level permissions based on user type and company role.
 *
 * This constant defines a mapping of permissions for different user types (e.g., customer, agent)
 * and their associated roles within a company. It specifies which actions each role is allowed to perform
 * using permission validation services.
 *
 * @const {PermissionTypesObject}
 * @note ETT-133 Permissions check based on the given table.
 */
export const appPermissions: PermissionTypesObject = {
	[CompanyType.customer]: {
		[CompanyMemberRole.viewer]: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
		],
		[CompanyMemberRole.editor]: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
			{ canCreateUnionPayment: PermissionValidationService.canCreateUnionPayment },
			{ canCreateVendorPayment: PermissionValidationService.canCreateVendorPayment },
			{ canImportPayment: PermissionValidationService.canImportPayment },
			{ canInvitePerformer: PermissionValidationService.canInvitePerformer }
		],
		[CompanyMemberRole.admin]: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
			{ canCreateUnionPayment: PermissionValidationService.canCreateUnionPayment },
			{ canCreateVendorPayment: PermissionValidationService.canCreateVendorPayment },
			{ canImportPayment: PermissionValidationService.canImportPayment },
			{ canAddEditDeleteMember: PermissionValidationService.canAddEditDeleteMember },
			{ canInvitePerformer: PermissionValidationService.canInvitePerformer }
		],
		[CompanyMemberRole.owner]: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
			{ canCreateUnionPayment: PermissionValidationService.canCreateUnionPayment },
			{ canCreateVendorPayment: PermissionValidationService.canCreateVendorPayment },
			{ canImportPayment: PermissionValidationService.canImportPayment },
			{ canAddEditDeleteMember: PermissionValidationService.canAddEditDeleteMember },
			{ canInvitePerformer: PermissionValidationService.canInvitePerformer },
			{ canEditProfile: PermissionValidationService.canEditProfile }
		]
	},
	[CompanyType.agent]: {
		[CompanyMemberRole.viewer]: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
			{ canViewClients: PermissionValidationService.canViewClients }
		],
		[CompanyMemberRole.editor]: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
			{ canViewClients: PermissionValidationService.canViewClients },
			{ canUploadDocsToClient: PermissionValidationService.canUploadDocsToClient },
			{ canAddEditDeleteClient: PermissionValidationService.canAddEditDeleteClient },
		],
		[CompanyMemberRole.admin]: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
			{ canViewClients: PermissionValidationService.canViewClients },
			{ canUploadDocsToClient: PermissionValidationService.canUploadDocsToClient },
			{ canAddEditDeleteClient: PermissionValidationService.canAddEditDeleteClient },
			{ canAddEditDeleteMember: PermissionValidationService.canAddEditDeleteMember }
		],
		[CompanyMemberRole.owner]: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
			{ canViewClients: PermissionValidationService.canViewClients },
			{ canUploadDocsToClient: PermissionValidationService.canUploadDocsToClient },
			{ canAddEditDeleteClient: PermissionValidationService.canAddEditDeleteClient },
			{ canAddEditDeleteMember: PermissionValidationService.canAddEditDeleteMember },
			{ canEditProfile: PermissionValidationService.canEditProfile }
		],
	},
	[CompanyType.talent]: {
		[CompanyMemberRole.owner]: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
			{ canCreateVendorPayment: PermissionValidationService.canCreateVendorPayment },
			{ canEditProfile: PermissionValidationService.canEditProfile }
		]
	}
};

/**
 * The idea behind these list is to restrict access to the corresponding page.
 * PermissionGuard check permissions of a current context and match them to the current page URL.
 * Permissions check is based on the appPermissions table (above) per user role.
 *
 * E.g. User can be in several contexts. Let's say one of the contexts is agent.
 * Agent should not being able to access payment creation screen.
 *
 * If page doesn't match the permissions, the user is redirected to a homepage.
 * By default, page access is allowed.
 *
 * IMPORTANT: Don't add the empty permission property if there is no need in checking it.
 * Additional explanation is available inside the PermissionGuard.
 */
export const appPermissionGuards: Array<NavigationInfo> = [
	{
		name: 'All Transactions',
		path: '/orders',
		children: [
			{
				name: 'Pay Performer',
				path: '/orders/create/union',
				permissions: [
					{ canCreateUnionPayment: PermissionValidationService.canCreateUnionPayment },
					{ isCustomer: PermissionValidationService.isCustomer }
				]
			},
			{
				name: 'Pay Vendor',
				path: '/orders/create/vendor',
				permissions: [
					{ canCreateVendorPayment: PermissionValidationService.canCreateVendorPayment }
				]
			},
			{
				name: 'Request Payment as Performer',
				path: '/orders/request/union',
				permissions: [
					{ canViewPayment: PermissionValidationService.canViewPayment },
					{ isTalent: PermissionValidationService.isTalent }
				]
			},
			{
				name: 'Request Payment as Vendor',
				path: '/orders/request/vendor',
				permissions: [
					{ canCreateVendorPayment: PermissionValidationService.canCreateVendorPayment }
				]
			},
			{
				name: ACTION_REQUIRED_QUEUE_NAME,
				path: '/orders/queue',
				permissions: [
					{ canViewPayment: PermissionValidationService.canViewPayment }
				]
			},
			{
				name: 'Bulk Payments',
				path: '/orders/import',
				permissions: [
					{ canImportPayment: PermissionValidationService.canImportPayment },
					{ isCustomer: PermissionValidationService.isCustomer }
				]
			},
		],
		permissions: [
			{ canViewPayment: PermissionValidationService.canViewPayment },
			{ isNotSuperAdmin: PermissionValidationService.isNotSuperAdmin }
		],
	},
	{
		name: 'Users',
		path: '/users',
		icon: 'people',
		permissions: [
			{ isSuperAdmin: PermissionValidationService.isSuperAdmin }
		],
	},
	{
		name: 'Manage Team',
		path: '/companies/team',
		permissions: [
			{ canViewMembers: PermissionValidationService.canViewMembers },
			// { canEditMembers: PermissionValidationService.canAddEditDeleteMember }
		],
	},
	{
		name: 'Contributors',
		path: '/companies',
		children: [
			{
				name: 'Clients',
				path: '/companies/clients',
				permissions: [
					{ canViewClients: PermissionValidationService.canViewClients }
				],
			},
			{
				name: 'Agents',
				path: '/companies/agents',
				permissions: [
					{ hasAgents: PermissionValidationService.hasAgents }
				],
			},
		],
		permissions: [
			{ canViewContributors: PermissionValidationService.canViewContributors },
		],
	},
	{
		name: 'Analytics & Reports',
		path: '/reports',
		children: [
			{
				name: 'Pay Report',
				path: '/reports',
				permissions: [
					{ isSuperAdmin: PermissionValidationService.isSuperAdmin }
				],
			},
			{
				name: 'Collect Report',
				path: '/reports/invoice',
				permissions: [
					{ isSuperAdmin: PermissionValidationService.isSuperAdmin }
				],
			},
		],
		permissions: [{
			isSuperAdmin: PermissionValidationService.isSuperAdmin
		}],
	},
	{
		name: 'Profile Info',
		path: '/profile/details',
		permissions: [
			{ canEditProfile: PermissionValidationService.canEditProfile }
		]
	},
	{
		name: 'Personal Settings',
		path: '/profile/taxation',
		permissions: [
			{ canEditProfile: PermissionValidationService.canEditProfile },
			{ canEditProfileTaxationInfo: PermissionValidationService.canEditProfileTaxationInfo }
		]
	},
	{
		name: 'Financial Settings',
		path: '/profile/banking',
		permissions: [
			{ canEditProfile: PermissionValidationService.canEditProfile },
			{ canEditProfileFinancialInfo: PermissionValidationService.canEditProfileFinancialInfo }
		]
	},
	{
		name: 'Preferences',
		path: '/profile/preferences',
		permissions: [
			{ canViewClients: PermissionValidationService.canViewClients }
		]
	}
];
